import { X, Anvil } from 'lucide-react';
import clsx from 'clsx';
import FilePreview from '../FilePreview';

interface EditorPanelProps {
  openTabs: string[];
  activeTab: string | null;
  files: Record<string, string>;
  onCloseTab: (path: string) => void;
  onSelectTab: (path: string) => void;
}

function getFileName(path: string): string {
  return path.split('/').pop() || path;
}

export default function EditorPanel({
  openTabs,
  activeTab,
  files,
  onCloseTab,
  onSelectTab,
}: EditorPanelProps) {
  if (openTabs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-[#1e1e1e] text-gray-600">
        <Anvil className="h-16 w-16 mb-4 opacity-20" />
        <p className="text-sm">Select a file to view</p>
        <p className="text-xs mt-1 text-gray-700">
          or try an assignment from the terminal below
        </p>
      </div>
    );
  }

  const activeContent = activeTab ? files[activeTab] : undefined;

  return (
    <div className="flex flex-col h-full bg-[#1e1e1e] overflow-hidden">
      {/* Tab bar */}
      <div className="flex items-center bg-[#252526] border-b border-dark-border overflow-x-auto scrollbar-thin">
        {openTabs.map((path) => (
          <div
            key={path}
            className={clsx(
              'group flex items-center gap-2 px-3 py-1.5 text-xs font-medium border-r border-dark-border cursor-pointer shrink-0 min-w-0',
              path === activeTab
                ? 'bg-[#1e1e1e] text-white border-t border-t-accent'
                : 'text-gray-500 hover:text-gray-300 bg-[#2d2d2d]',
            )}
            onClick={() => onSelectTab(path)}
          >
            <span className="truncate max-w-[120px]">
              {getFileName(path)}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCloseTab(path);
              }}
              className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-surface-200 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab && activeContent !== undefined ? (
          <FilePreview path={activeTab} content={activeContent} />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-600 text-sm">
            File not found
          </div>
        )}
      </div>
    </div>
  );
}
